import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { storedData } from './CheckoutPageSessionHelpers';
import { 
  createResourceLocatorString,
  findRouteByRouteName  
} from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { ensureCurrentUser } from '../../util/data';
import { FormattedMessage } from '../../util/reactIntl';

// Use Stripe hooks from the existing context (don't load Stripe again)
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import css from './CheckoutPageWithPayment.module.css';

// Custom Stripe Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

/**
 * IndytendCheckoutForm - The main checkout form component
 * Handles payment processing, booking confirmation, and order submission
 * Uses existing Stripe context from app-level Elements provider
 */
const IndytendCheckoutForm = ({ 
  listing, 
  orderData,
  onSubmitOrder,
  onSubmitCardDetails,
  paymentInProgress,
  currentUser,
  submitButtonText,
  totalPrice,
  currency 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const intl = useIntl();
  
  const [paymentError, setPaymentError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded
      console.error('Stripe has not loaded yet');
      return;
    }

    setProcessingPayment(true);
    setPaymentError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: currentUser?.attributes?.profile?.displayName || 'Customer',
          email: currentUser?.attributes?.email,
        },
      });

      if (error) {
        setPaymentError(error.message);
        setProcessingPayment(false);
        return;
      }

      // Submit the order with the payment method
      await onSubmitOrder({
        ...orderData,
        paymentMethod: paymentMethod.id,
      });

    } catch (err) {
      setPaymentError(err.message || 'An error occurred during payment');
    }
    
    setProcessingPayment(false);
  };

  return (
    <form onSubmit={handleSubmit} className={css.checkoutForm}>
      <div className={css.paymentSection}>
        <h3 className={css.sectionTitle}>
          <FormattedMessage id="CheckoutPageWithPayment.paymentTitle" />
        </h3>
        
        <div className={css.cardElementContainer}>
          <CardElement
            options={cardElementOptions}
            className={css.cardElement}
          />
        </div>
        
        {paymentError && (
          <div className={css.error}>
            {paymentError}
          </div>
        )}
      </div>

      <div className={css.submitSection}>
        <button 
          type="submit" 
          disabled={!stripe || processingPayment || paymentInProgress}
          className={css.submitButton}
        >
          {processingPayment ? (
            <FormattedMessage id="CheckoutPageWithPayment.processing" />
          ) : (
            submitButtonText || <FormattedMessage id="CheckoutPageWithPayment.submitPayment" />
          )}
        </button>
      </div>
    </form>
  );
};

/**
 * Main CheckoutPageWithPayment Component
 * This component uses the Stripe context provided by the app-level Elements wrapper
 */
const CheckoutPageWithPaymentComponent = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  
  const {
    scrollingDisabled,
    currentUser,
    orderData,
    listing,
    params,
    onManageDisableScrolling,
    onSubmitOrder,
    ...rest
  } = props;

  const pageTitle = intl.formatMessage(
    { id: 'CheckoutPageWithPayment.title' },
    { listingTitle: listing?.attributes?.title || '' }
  );

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn 
        topbar={<TopbarContainer />} 
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <div className={css.content}>
            <IndytendCheckoutForm
              listing={listing}
              orderData={orderData}
              onSubmitOrder={onSubmitOrder}
              currentUser={currentUser}
              {...rest}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser: ensureCurrentUser(currentUser),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const CheckoutPageWithPayment = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CheckoutPageWithPaymentComponent);

export default CheckoutPageWithPayment;
